import { memo, useMemo } from 'react'
import type { DayContentProps } from 'react-day-picker'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	Calendar,
	type CalendarProps,
} from '@cocoonspace/ui/components/calendar'
import { useLingui } from '@lingui/react/macro'
import { fr } from 'date-fns/locale/fr'

import { useAuth } from '~/providers/auth-provider'
import { useMultidatesContext } from './provider'

const styles: CalendarProps['classNames'] = {
	button:
		'!text-sm rounded-full h-[45px] w-[45px] flex items-center justify-center cursor-pointer',
	cell: 'p-[1px] h-[50px] w-[50px]',
	day_disabled: 'opacity-30 hover:!bg-transparent !cursor-default',
	head_cell: 'text-sm h-[30px] !w-[30px] font-normal opacity-50 p-1',
	month: 'px-4',
	table: 'mx-0 w-full mt-2',
}

const locales = {
	fr,
}

const DayContent = memo(({ date }: DayContentProps) => {
	const { i18n } = useLingui()
	const { pricesQuery } = useMultidatesContext()

	const price = useMemo(() => {
		const currentDay = dateTimeUtils(date).customFormat('dateTech')
		const currentPrice = pricesQuery.data?.pricesByDay[currentDay]
		return currentPrice?.price_day_discounted ?? currentPrice?.price_day
	}, [date, pricesQuery.data?.pricesByDay])

	const isFull = Number(price?.toString().slice(-2))

	return (
		<div className='flex flex-col gap-y-1 leading-none'>
			{date.getDate()}

			{price !== undefined && (
				<div className='text-xxs leading-none'>
					{i18n.number(price / 100, {
						style: 'currency',
						currency: 'EUR',

						...(!isFull && {
							minimumFractionDigits: 0,
							maximumFractionDigits: 2,
							trailingZeroDisplay: 'auto',
						}),
					})}
				</div>
			)}
		</div>
	)
})

DayContent.displayName = 'DayContent'

export const StepMultidateCalendar = memo(
	({ maxDate }: { maxDate: string }) => {
		const { isAuth } = useAuth()
		const { i18n } = useLingui()

		const { pricesQuery, bookingDays, setBookingDays } = useMultidatesContext()

		const maxDateNative = useMemo(
			() => dateTimeUtils(maxDate).toDate(),
			[maxDate],
		)

		const isDayDisabled = useMemo(() => {
			const pricesByDay = pricesQuery.data?.pricesByDay

			return (date: Date) => {
				if (pricesQuery.isLoading || !pricesByDay) return true

				const day = dateTimeUtils(date).customFormat('dateTech')

				return !pricesByDay[day]
			}
		}, [pricesQuery.data?.pricesByDay, pricesQuery.isLoading])

		return (
			<div className='max-h-[478px] w-full overflow-x-hidden overflow-y-scroll overscroll-contain pb-4'>
				<Calendar
					mode='multiple'
					numberOfMonths={13}
					classNames={styles}
					disabled={isDayDisabled}
					components={isAuth ? { DayContent: DayContent as any } : undefined}
					disableNavigation
					fromDate={dateTimeUtils().toDate()}
					locale={locales[i18n.locale as 'fr']}
					selected={bookingDays.map((day) => dateTimeUtils(day).toDate())}
					toDate={maxDateNative}
					onSelect={(days) => {
						const daysString =
							days
								?.map((day) => dateTimeUtils(day).customFormat('dateTech'))
								.filter((day) => pricesQuery.data?.pricesByDay[day]) ?? []

						setBookingDays(daysString)
					}}
				/>
			</div>
		)
	},
)

StepMultidateCalendar.displayName = 'StepMultidateCalendar'
